import React, { CSSProperties, forwardRef } from 'react';
import cx from 'classnames';
import { Sprinkles } from '@mutiny-pkg/dumpster-ui/theme/sprinkles.css';
import { vars } from '@mutiny-pkg/dumpster-ui/theme/theme.css';
import { Box } from '../Box';
import * as styles from './Typography.css';

type TypographySprinklesProps = Pick<Sprinkles, 'font' | 'fontWeight' | 'margin' | 'whiteSpace'>;
type ElementTags = 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'span' | 'li';

export type TypographyProps = {
  truncated?: boolean;
  capitalize?: boolean;
  wrapText?: boolean;
  uppercase?: boolean;
  noSelect?: boolean;
} & TypographySprinklesProps &
  Omit<React.HTMLAttributes<HTMLElement>, keyof NonNullable<Sprinkles> | 'style'> & {
    as?: ElementTags;
    color?: keyof typeof vars.color;
    fontStyle?: CSSProperties['fontStyle'];
    style?: Omit<CSSProperties, 'font' | 'fontWeight' | 'lineHeight' | 'fontFamily' | 'whiteSpace'>;
  };

export const typographyMap: Record<NonNullable<Pick<TypographySprinklesProps, 'font'>['font']>, ElementTags> = {
  heading1: 'h1',
  heading2: 'h2',
  heading3: 'h3',
  body1: 'p',
  caption1: 'span',
  caption2: 'span',
  metricData1: 'span',
  metricData2: 'span',
  metricData3: 'span',
  metricData4: 'span',
};

export const Typography = forwardRef<HTMLElement, TypographyProps>(
  (
    {
      font = 'body1',
      as,
      children,
      fontStyle,
      margin,
      truncated = false,
      capitalize = false,
      wrapText = false,
      uppercase = false,
      noSelect = false,
      className,
      style,
      ...props
    },
    ref,
  ) => {
    return (
      <Box
        as={as || typographyMap[font]}
        font={font}
        margin={margin}
        className={cx(className, styles.self({ truncated, capitalize, wrapText, uppercase, noSelect }))}
        lang={wrapText ? 'en' : undefined}
        style={{
          ...style,
          fontStyle,
        }}
        ref={ref}
        {...props}
      >
        {children}
      </Box>
    );
  },
);

Typography.displayName = 'Typography';
